import { Grid, Typography, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useLazyQuery } from "@apollo/client";
import { GET_REPORT } from "../graphql/queries";
import logo from "../assets/NSCC_logo.png";
import altlogo from "../assets/SolverEDGE.svg";
import { useLocation, useParams } from "react-router-dom";
import { AuthenticatedContext } from "../App";
import HorizontalStackedBarGraph from "../components/graphs/HorizontalStackedBarGraph";
import AnswerScatterGraph from "../components/graphs/AnswerScatterGraph";
import TimeBarGraph from "../components/graphs/TimeBarGraph";

const PostAssessmentReport = () => {
  const [reportData, setReportData] = useState(null);
  const [todayDate, setTodayDate] = useState("");
  const [studentName, setStudentName] = useState("Chatswood Y3 X");

  const { user } = useContext(AuthenticatedContext);

  const location = useLocation();
  const { program, id } = useParams();

  const adjustScore = (score) => Math.min(100, Math.max(0, score *  16.67 + 50));

  const [getReport] = useLazyQuery(GET_REPORT, {
    onCompleted: (report) => {
      let sortedResults = {};
      const results = report.getReport.ps_results;
      
      const maths = results.filter(r => r.psType === "Mathematics");
      let mathResults = [];
      for (let mathResult of maths) {
        const strandBreakdown = mathResult.strand.split("|");
        const index = mathResults.findIndex(m => m.name === strandBreakdown[2]);
        if (index === -1) {
          mathResults.push({
            name: strandBreakdown[2],
            ...(strandBreakdown[0] === "new") ? { "Post-Assessment": adjustScore(mathResult.score) } : { "Pre-Assessment": adjustScore(mathResult.score) }
          });
        } else {
          if (strandBreakdown[0] === "new") {
            mathResults[index]["Post-Assessment"] = adjustScore(mathResult.score);
          } else {
            mathResults[index]["Pre-Assessment"] = adjustScore(mathResult.score);
          }
        }
      }

      const strat = results.filter(r => r.psType === "Method & Strategy");
      let stratResults = [];
      for (let stratResult of strat) {
        const strandBreakdown = stratResult.strand.split("|");
        const index = stratResults.findIndex(s => s.name === strandBreakdown[2]);
        if (index === -1) {
          stratResults.push({
            name: strandBreakdown[2],
            ...(strandBreakdown[0] === "new") ? { "Post-Assessment": adjustScore(stratResult.score) } : { "Pre-Assessment": adjustScore(stratResult.score) }
          });
        } else {
          if (strandBreakdown[0] === "new") {
            stratResults[index]["Post-Assessment"] = adjustScore(stratResult.score);
          } else {
            stratResults[index]["Pre-Assessment"] = adjustScore(stratResult.score);
          }
        }
      }

      const diag = results.filter(r => r.psType === "Perseverence");
      let diagResults = [];
      for (let diagResult of diag) {
        const strandBreakdown = diagResult.strand.split("|");
        const index = diagResults.findIndex(d => d.name === strandBreakdown[2]);
        if (index === -1) {
          diagResults.push({
            name: strandBreakdown[2],
            ...(strandBreakdown[0] === "new") ? { "Post-Assessment": diagResult.score } : { "Pre-Assessment": diagResult.score }
          });
        } else {
          if (strandBreakdown[0] === "new") {
            diagResults[index]["Post-Assessment"] = diagResult.score;
          } else {
            diagResults[index]["Pre-Assessment"] = diagResult.score;
          }
          diagResults[index].name = "Perseverence";
        }
      }
      function compare(a, b) {
        if (a.name === "Overall") {
          return 1;
        } else if (b.name === "Overall") {
          return -1;
        } else {
          return a.name - b.name;
        }
      }

      const mathSit = results.filter(r => r.psType === "Maths");
      let mathSitResult = {};
      for (let result of mathSit) {
        const strandBreakdown = result.strand.split("|");
        if (!(strandBreakdown[2] in mathSitResult)) {
          mathSitResult[strandBreakdown[2]] = {
            oldCorrect: [],
            oldIncorrect: [],
            newCorrect: [],
            newIncorrect: [],
            times: [
              {
                name: "<0:15",
                old: 0,
                new: 0,
                
              },
              {
                name: "<0:30",
                old: 0,
                new: 0,
              },
              {
                name: "<0:45",
                old: 0,
                new: 0,
              },
              {
                name: "<1:00",
                old: 0,
                new: 0,
              },
              {
                name: "<1:15",
                old: 0,
                new: 0,
              },
              {
                name: "<1:30",
                old: 0,
                new: 0,
              },
              {
                name: "<1:45",
                old: 0,
                new: 0,
              },
              {
                name: "<2:00",
                old: 0,
                new: 0,
              },
              {
                name: ">=2:00",
                old: 0,
                new: 0,
              }
            ]
          };
          // mathSitResult[strandBreakdown[2]] = {
          //   new: [],
          //   old: []
          // };
        }
        let category;
        if (result.score < 15000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<0:15");
        } else if (result.score < 30000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<0:30");
        } else if (result.score < 45000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<0:45");
        } else if (result.score < 60000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:00");
        } else if (result.score < 75000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:15");
        } else if (result.score < 90000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:30");
        } else if (result.score < 105000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:45");
        } else if (result.score < 120000) {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<2:00");
        } else {
          category = mathSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === ">=2:00");
        }
        if (strandBreakdown[0] === "old") {
          mathSitResult[strandBreakdown[2]]["times"][category]["old"]++;
          if (strandBreakdown[3] === "0") {
            mathSitResult[strandBreakdown[2]]["oldIncorrect"].push(result.score);
          } else if (strandBreakdown[3] === "1") {
            mathSitResult[strandBreakdown[2]]["oldCorrect"].push(result.score);
          }
          // mathSitResult[strandBreakdown[2]]["old"].push({
          //   time: result.score,
          //   mark: Number(strandBreakdown[3])
          // });
        } else {
          mathSitResult[strandBreakdown[2]]["times"][category]["new"]++;
          if (strandBreakdown[3] === "0") {
            mathSitResult[strandBreakdown[2]]["newIncorrect"].push(result.score);
          } else if (strandBreakdown[3] === "1") {
            mathSitResult[strandBreakdown[2]]["newCorrect"].push(result.score);
          }
          // mathSitResult[strandBreakdown[2]]["new"].push({
          //   time: result.score,
          //   mark: Number(strandBreakdown[3])
          // });
        }
      }
      const average = array => array.length === 0 ? 0 : array.reduce((a, b) => a + b) / array.length;
      const median = array => {

        if (array.length === 0) {
          return 0;
        }
      
        // Sorting values, preventing original array
        // from being mutated.
        array = [...array].sort((a, b) => a - b);
      
        const half = Math.floor(array.length / 2);
      
        return (array.length % 2
          ? array[half]
          : (array[half - 1] + array[half]) / 2
        );
      }

      for (let strand of Object.keys(mathSitResult)) {
        mathSitResult[strand].oldCorrectAvg = average(mathSitResult[strand].oldCorrect);
        mathSitResult[strand].oldIncorrectAvg = average(mathSitResult[strand].oldIncorrect);
        mathSitResult[strand].oldCorrectMed = median(mathSitResult[strand].oldCorrect);
        mathSitResult[strand].oldIncorrectMed = median(mathSitResult[strand].oldIncorrect);
        mathSitResult[strand].newCorrectAvg = average(mathSitResult[strand].newCorrect);
        mathSitResult[strand].newIncorrectAvg = average(mathSitResult[strand].newIncorrect);
        mathSitResult[strand].newCorrectMed = median(mathSitResult[strand].newCorrect);
        mathSitResult[strand].newIncorrectMed = median(mathSitResult[strand].newIncorrect);
      }

      const stratSit = results.filter(r => r.psType === "Strat");
      let stratSitResult = {};
      for (let result of stratSit) {
        const strandBreakdown = result.strand.split("|");
        if (!(strandBreakdown[2] in stratSitResult)) {
          stratSitResult[strandBreakdown[2]] = {
            oldCorrect: [],
            oldIncorrect: [],
            newCorrect: [],
            newIncorrect: [],
            times: [
              {
                name: "<0:15",
                old: 0,
                new: 0,
                
              },
              {
                name: "<0:30",
                old: 0,
                new: 0,
              },
              {
                name: "<0:45",
                old: 0,
                new: 0,
              },
              {
                name: "<1:00",
                old: 0,
                new: 0,
              },
              {
                name: "<1:15",
                old: 0,
                new: 0,
              },
              {
                name: "<1:30",
                old: 0,
                new: 0,
              },
              {
                name: "<1:45",
                old: 0,
                new: 0,
              },
              {
                name: "<2:00",
                old: 0,
                new: 0,
              },
              {
                name: ">=2:00",
                old: 0,
                new: 0,
              }
            ]
          };
          // stratSitResult[strandBreakdown[2]] = {
          //   new: [],
          //   old: []
          // };
        }
        let category;
        if (result.score < 15000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<0:15");
        } else if (result.score < 30000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<0:30");
        } else if (result.score < 45000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<0:45");
        } else if (result.score < 60000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:00");
        } else if (result.score < 75000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:15");
        } else if (result.score < 90000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:30");
        } else if (result.score < 105000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<1:45");
        } else if (result.score < 120000) {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === "<2:00");
        } else {
          category = stratSitResult[strandBreakdown[2]]["times"].findIndex(m => m.name === ">=2:00");
        }
        if (strandBreakdown[0] === "old") {
          stratSitResult[strandBreakdown[2]]["times"][category]["old"]++;
          if (strandBreakdown[3] === "0") {
            stratSitResult[strandBreakdown[2]]["oldIncorrect"].push(result.score);
          } else if (strandBreakdown[3] === "1") {
            stratSitResult[strandBreakdown[2]]["oldCorrect"].push(result.score);
          }
          // stratSitResult[strandBreakdown[2]]["old"].push({
          //   time: result.score,
          //   mark: Number(strandBreakdown[3])
          // });
        } else {
          stratSitResult[strandBreakdown[2]]["times"][category]["new"]++;
          if (strandBreakdown[3] === "0") {
            stratSitResult[strandBreakdown[2]]["newIncorrect"].push(result.score);
          } else if (strandBreakdown[3] === "1") {
            stratSitResult[strandBreakdown[2]]["newCorrect"].push(result.score);
          }
          // stratSitResult[strandBreakdown[2]]["new"].push({
          //   time: result.score,
          //   mark: Number(strandBreakdown[3])
          // });
        }
      }

      for (let strand of Object.keys(stratSitResult)) {
        stratSitResult[strand].oldCorrectAvg = average(stratSitResult[strand].oldCorrect);
        stratSitResult[strand].oldIncorrectAvg = average(stratSitResult[strand].oldIncorrect);
        stratSitResult[strand].oldCorrectMed = median(stratSitResult[strand].oldCorrect);
        stratSitResult[strand].oldIncorrectMed = median(stratSitResult[strand].oldIncorrect);
        stratSitResult[strand].newCorrectAvg = average(stratSitResult[strand].newCorrect);
        stratSitResult[strand].newIncorrectAvg = average(stratSitResult[strand].newIncorrect);
        stratSitResult[strand].newCorrectMed = median(stratSitResult[strand].newCorrect);
        stratSitResult[strand].newIncorrectMed = median(stratSitResult[strand].newIncorrect);
      }

      sortedResults.Mathematics = [...mathResults.sort(compare)];
      sortedResults["Method & Strategy"] = [...stratResults.sort(compare)];
      sortedResults.Perseverence = [...diagResults];
      sortedResults.MathSitting = {...mathSitResult};
      sortedResults.StratSitting = {...stratSitResult};
      
      setReportData(sortedResults);
      setStudentName(
        `${report.getReport.Student.givenname} ${report.getReport.Student.surname}`
      );
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (location.pathname === `/postreport/${program}`) {
      getReport({
        variables: {
          program_id: Number(program),
          student_id: Number(JSON.parse(user).student_id),
        },
      });
    } else if (location.pathname === `/postreport/${program}/${id}`) {
      getReport({
        variables: {
          program_id: Number(program),
          student_id: Number(id),
        },
      });
    }

    const today = new Date();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    setTodayDate(
      `${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`
    );
  }, [id, location.pathname, program, user]);

  function toTitleCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  }

  return reportData && (
    <>
      <Box sx={{ margin: 3 }}>
        <Stack direction="row" justifyContent="space-between">
          {user &&
          ((JSON.parse(user).student_region &&
            JSON.parse(user).student_region === 8) ||
            (JSON.parse(user).region_id &&
              JSON.parse(user).region_id === 8)) ? (
            <img
              src={altlogo}
              alt="SolverEdge Logo"
              style={{ marginRight: "32px", height: "60px", maxWidth: "100px" }}
            />
          ) : (
            <img
              src={logo}
              alt="NSCC Logo"
              style={{ marginRight: "32px", height: "60px", maxWidth: "100px" }}
            />
          )}
          <Typography variant="h4" component="div">
            Problem Solving Post-Assessment Report
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          <Typography variant="h5" component="div">
            {todayDate}
          </Typography>
        </Stack>
        <Typography variant="body1" component="div" sx={{ fontSize: 16 }}>
          Dear {toTitleCase(studentName)},
        </Typography>
        <Typography variant="body1" component="div" sx={{ fontSize: 16 }}>
          Thank you for sitting the Problem Solving Post-Assessment.<br />
          This report is best viewed on desktop computer/laptop or device with
          screen size of 10 inches or more. If the achievement scale below does
          not load correctly, we recommend that you download the PDF report onto
          a desktop computer/laptop and open it using PDF reader software.
        </Typography>
        <br />
        <Typography variant="h5" component="div">
          Your Achievement
        </Typography>

        <Typography
          variant="body1"
          component="div"
          sx={{ fontSize: 16 }}
        >
          Your results in your recent Problem Solving Post-Assessment (coloured green) has been compared to your Initial Pre-Assessment (coloured blue) to track progress in your Ability level.<br />
          Your Ability level is your competency or proficiency in specific problem solving traits or skills. The higher your Ability level, the stronger is your problem solving skill.
        </Typography>
        <br />
        <Typography variant="h5" component="div">
          1. Strategy & Method
        </Typography>
        <br />
        <Stack direction="row" justifyContent="space-between">
          <Box />
          {reportData && (
            <Stack direction="column">
              <HorizontalStackedBarGraph
                data={reportData["Method & Strategy"]}
              />
              {/* {
                Object.keys(reportData["StratSitting"]).map(r => {
                  return (
                    <Stack direction="column">
                      {r}
                      <AnswerScatterGraph
                        data={reportData["StratSitting"][r]}
                      />
                      <TimeBarGraph
                        data={reportData["StratSitting"][r].times}
                      />
                      <Typography variant="body1">Pre-Assessment Average Time for Correct Answers: {reportData["StratSitting"][r].oldCorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Average Time for Correct Answers: {reportData["StratSitting"][r].newCorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Pre-Assessment Median Time for Correct Answers: {reportData["StratSitting"][r].oldCorrectMed / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Median Time for Correct Answers: {reportData["StratSitting"][r].newCorrectMed / 1000}s</Typography>
                      <Typography variant="body1">Pre-Assessment Average Time for Incorrect Answers: {reportData["StratSitting"][r].oldIncorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Average Time for Incorrect Answers: {reportData["StratSitting"][r].newIncorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Pre-Assessment Median Time for Incorrect Answers: {reportData["StratSitting"][r].oldIncorrectMed / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Median Time for Incorrect Answers: {reportData["StratSitting"][r].newIncorrectMed / 1000}s</Typography>
                    </Stack>
                  )
                })
              } */}
            </Stack>
          )}
          <Box />
        </Stack>
        <Typography variant="h5" component="div">
          2. Mathematical Ability
        </Typography>
        <br />
        <Stack direction="row" justifyContent="space-between">
          <Box />
          {reportData && (
            <Stack direction="column">
              <HorizontalStackedBarGraph
                data={reportData["Mathematics"]}
              />
              {/* {
                Object.keys(reportData["MathSitting"]).map(r => {
                  return (
                    <Stack direction="column">
                      {r}
                      <AnswerScatterGraph
                        data={reportData["MathSitting"][r]}
                      />
                      <TimeBarGraph
                        data={reportData["MathSitting"][r].times}
                      />
                      <Typography variant="body1">Pre-Assessment Average Time for Correct Answers: {reportData["MathSitting"][r].oldCorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Average Time for Correct Answers: {reportData["MathSitting"][r].newCorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Pre-Assessment Median Time for Correct Answers: {reportData["MathSitting"][r].oldCorrectMed / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Median Time for Correct Answers: {reportData["MathSitting"][r].newCorrectMed / 1000}s</Typography>
                      <Typography variant="body1">Pre-Assessment Average Time for Incorrect Answers: {reportData["MathSitting"][r].oldIncorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Average Time for Incorrect Answers: {reportData["MathSitting"][r].newIncorrectAvg / 1000}s</Typography>
                      <Typography variant="body1">Pre-Assessment Median Time for Incorrect Answers: {reportData["MathSitting"][r].oldIncorrectMed / 1000}s</Typography>
                      <Typography variant="body1">Post-Assessment Median Time for Incorrect Answers: {reportData["MathSitting"][r].newIncorrectMed / 1000}s</Typography>
                    </Stack>
                  )
                })
              } */}
              
            </Stack>
          )}
          <Box />
        </Stack>
        {/* <Typography variant="h5" component="div">
          3. Perseverence
        </Typography>
        <br />
        <Stack direction="row" justifyContent="space-between">
          <Box />
          { (
            <HorizontalStackedBarGraph
              data={reportData["Perseverence"]}
            />
          )}
          <Box />
        </Stack>
        <br />
        <br /> */}
        {user &&
          ((JSON.parse(user).student_region &&
            JSON.parse(user).student_region === 8) ||
            (JSON.parse(user).region_id &&
              JSON.parse(user).region_id === 8)) ? (
              <>
                <Typography variant="body1" component="div" sx={{ fontSize: 16 }}>      
                  <strong>Congratulations on completing this term!</strong> Problem-solving and critical thinking are not just skills for the classroom—they're <strong>lifelong tools</strong> that grow stronger with <strong>consistent practice and reinforcement</strong>. Every challenge you tackle builds your ability to think clearly, adapt, and succeed.<br />
                  <strong>Keep sharpening these skills!</strong> Join us next term to dive deeper into new topics, refine your strategies, and <strong>continue your journey toward mastery</strong>. Progress comes from persistence!
                </Typography>
                <br />
              </>
          ) : <></>
        }
        <Typography variant="body1" component="div" sx={{ fontSize: 16 }}>
          Should you require further assistance with interpretation of the results, please email us on {user &&
          ((JSON.parse(user).student_region &&
            JSON.parse(user).student_region === 8) ||
            (JSON.parse(user).region_id &&
              JSON.parse(user).region_id === 8)) ? "info@solveredge.com" : "problemsolving@north-shore.com.au"
          }
        </Typography>
      </Box>
    </>
  )  
};

export default PostAssessmentReport;
